import { useState, useEffect } from 'react';

import { AppIcon, AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  Chip,
  TextField,
  Theme,
  SxProps,
  FilterOptionsState,
  Box,
  Checkbox,
} from '@mui/material';
import { ControllerRenderProps, FieldError, FieldValues } from 'react-hook-form';

import { BackrsFormField } from './types';

const determineIsOptionEqualToValue = (
  option: OptionType,
  value: OptionType | OptionType[],
  field: BackrsFormField,
) => {
  if (Array.isArray(value) && field.autoCompleteEndpoint) {
    return value.some((v) => v.value === option.value);
  }

  if (Array.isArray(value)) {
    return value.some((v) => v.value === option.value);
  }

  // value is selection, option is list of possibilities
  if (option.value === value.value) {
    return true;
  }

  return false;
};

const handleFilterOptions = (
  options: OptionType[],
  state: FilterOptionsState<OptionType>,
  field: BackrsFormField,
) => {
  if (state.inputValue.length < 4 && field.autoCompleteEndpoint) {
    return [];
  }

  return options;
};

type OptionType = Readonly<{
  label: string;
  value: string;
  icon?: AppIcons;
}>;

type BackrsFormAutoCompleteProps = Readonly<{
  isMultiple: boolean;
  autoCompleteOptions: OptionType[];
  options: OptionType[];
  field: BackrsFormField;
  controllerField: ControllerRenderProps<FieldValues, string>;
  groupedFields: Record<string, BackrsFormField[]>;
  index: number;
  error: FieldError | undefined;
  groupKey: string;
}>;

export const BackrsFormAutoComplete = ({
  isMultiple,
  autoCompleteOptions,
  field,
  options,
  controllerField,
  groupedFields,
  index,
  error,
  groupKey,
}: BackrsFormAutoCompleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const [localOptions, setLocalOptions] = useState<OptionType[]>(autoCompleteOptions);

  const sourceOptions = field.autoCompleteEndpoint ? localOptions : options;

  // Create option objects for values in the form
  const valueOptionsFromForm =
    isMultiple && Array.isArray(controllerField.value)
      ? controllerField.value.map((value) => {
          // First try to find in source options
          const existingOption = sourceOptions.find((opt) => opt.value === value);

          if (existingOption) return existingOption;

          // If not found, create a new option
          return { label: value, value };
        })
      : [];

  // Combine with source options if not found
  const selectedOptions = isMultiple
    ? Array.isArray(controllerField.value)
      ? valueOptionsFromForm
      : []
    : sourceOptions.find((option) => option.value === controllerField.value) || null;

  // Update local options when form values or autocomplete options change
  useEffect(() => {
    // Add any form values not in options to the options list
    if (isMultiple && Array.isArray(controllerField.value) && controllerField.value.length > 0) {
      const newOptions = [...autoCompleteOptions];

      controllerField.value.forEach((value) => {
        // Check if this value is already in options
        const exists = newOptions.some((option) => option.value === value);

        if (!exists) {
          newOptions.push({ label: value, value });
        }
      });

      setLocalOptions(newOptions);
    } else {
      setLocalOptions(autoCompleteOptions);
    }
  }, [autoCompleteOptions, controllerField.value, isMultiple]);

  return (
    <Autocomplete<OptionType, boolean, boolean, boolean>
      multiple={isMultiple}
      options={localOptions}
      isOptionEqualToValue={(option, value) =>
        determineIsOptionEqualToValue(option as OptionType, value, field)
      }
      filterOptions={(options, state) => handleFilterOptions(options as OptionType[], state, field)}
      noOptionsText={field.autoCompleteEndpoint ? 'Please enter at least 4 characters' : ''}
      {...(!field.autoCompleteEndpoint && {
        value: selectedOptions,
      })}
      {...(field.autoCompleteEndpoint && {
        inputValue,
        defaultValue: isMultiple ? valueOptionsFromForm : null,
      })}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
        field.onInputChange?.(newInputValue, field.name || '');
      }}
      onChange={(_, newValue) => {
        controllerField.onChange(
          Array.isArray(newValue)
            ? newValue.map((option) => (typeof option === 'string' ? option : option.value))
            : (typeof newValue === 'string' ? newValue : newValue?.value) || '',
        );

        // Force update local state to match the new value
        if (isMultiple) {
          const newOptions = Array.isArray(newValue)
            ? newValue.map((option) =>
                typeof option === 'string'
                  ? { label: option, value: option }
                  : (option as OptionType),
              )
            : [];

          setLocalOptions((prev) => {
            // Add any newly selected options
            const updatedOptions = [...prev];

            newOptions.forEach((option) => {
              if (!updatedOptions.some((o) => o.value === option.value)) {
                updatedOptions.push(option);
              }
            });

            return updatedOptions;
          });
        }
      }}
      sx={autoCompleteSx((Array.isArray(selectedOptions) ? selectedOptions : []).length || 0)}
      clearIcon={<AppIcon iconName="XClose" diameter="12px" color="Gray/500" />}
      /**
       *
       * FOR MULTIPLE AUTO COMPLETES, WE RENDER CHIPS
       */
      {...(isMultiple === false && field.autoCompleteEndpoint ? { freeSolo: true } : {})}
      renderOption={(props, option, { selected }) => {
        const customProps = {
          ...props,
          style: {
            ...props.style,
            backgroundColor: 'transparent',
          },
        };

        return (
          <li {...customProps}>
            {isMultiple && (
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckIcon />}
                style={{ marginRight: 8 }}
                checked={selected}
                disableRipple
              />
            )}
            {option.label}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index: number) => {
          const { onDelete, ...tagProps } = getTagProps({ index });
          const optionObj = option as OptionType;

          return (
            <Chip
              variant="tag"
              label={
                optionObj.label.length > 20 ? `${optionObj.label.slice(0, 10)}...` : optionObj.label
              }
              type="trailing"
              size="md"
              sx={chipSx(!!optionObj.icon || false)}
              icon={
                optionObj.icon ? (
                  <Box sx={iconContainerStyle}>
                    <AppIcon
                      iconName={optionObj.icon}
                      diameter="12px"
                      color="Colors/Foreground/fg-quaternary"
                    />
                  </Box>
                ) : undefined
              }
              deleteIcon={
                <Box sx={deleteIconContainerStyle}>
                  <AppIcon
                    iconName="XClose"
                    diameter="12px"
                    color={optionObj.icon ? 'Colors/Foreground/fg-quaternary' : 'Blue/400'}
                  />
                </Box>
              }
              {...tagProps}
              key={`${groupKey}-${field.name}-${optionObj.value}-${index}`}
              onDelete={(event) => {
                // Call the original onDelete from getTagProps
                if (onDelete) {
                  onDelete(event);
                }

                // As a fallback, also handle directly
                const currentValues = Array.isArray(controllerField.value)
                  ? controllerField.value
                  : [];

                const updatedValue = currentValues.filter((v: string) => v !== optionObj.value);

                controllerField.onChange(updatedValue);
              }}
            />
          );
        })
      }
      /**
       * THE VISUAL FOR THE AUTO COMPLETE IS A TEXT FIELD
       */
      renderInput={(params) => (
        <TextField
          {...params}
          id={field.name}
          name={field.name}
          label={field.label}
          error={!!error?.message}
          helperText={error?.message}
          placeholder={
            Array.isArray(selectedOptions) && selectedOptions.length > 0 ? '' : field.placeholder
          }
          sx={selectTextFieldStyle(
            index === Object.entries(groupedFields).length - 1,
            field.divider || false,
          )}
        />
      )}
      disableCloseOnSelect={isMultiple}
    />
  );
};

const selectTextFieldStyle =
  (isLastItem: boolean, divider: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    mb: {
      xs: backrs.spacing['spacing-md'],
      sm: backrs.spacing[isLastItem ? 'spacing-md' : 'spacing-2xl'],
    },

    ...(divider && {
      mb: backrs.spacing['spacing-none'],
    }),

    width: '100%',

    '& .MuiAutocomplete-inputRoot': {
      paddingY: `${backrs.spacing['spacing-xs']}`,
      paddingX: `${backrs.spacing['spacing-md']}`,
      gap: `${backrs.spacing['spacing-none']}`,
    },
  });

const chipSx =
  (icon: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    backgroundColor: backrs.colors['Blue/50'],
    border: `1px solid ${backrs.colors['Blue/200']}`,
    color: backrs.colors['Blue/700'],
    ...(icon && {
      backgroundColor: backrs.colors['Colors/Base/White'],
      border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
      color: backrs.colors['Colors/Text/text-secondary'],
    }),

    '& .MuiChip-icon': {
      marginRight: backrs.spacing['spacing-none'],
      marginLeft: backrs.spacing['spacing-none'],
    },
  });

const iconContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-hover'],
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  border: `0.5px solid ${backrs.colors['Colors/Base/Black']}14`,
});

const deleteIconContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  margin: `${backrs.spacing['spacing-none']} !important`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const autoCompleteSx =
  (selectedItemsCount: number): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    width: '100%',
    padding: `${backrs.spacing['spacing-none']}`,
    '& .MuiAutocomplete-root': {
      paddingY: `${backrs.spacing['spacing-none']} !important`,
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingY: `${backrs.spacing[selectedItemsCount > 2 ? 'spacing-md' : 'spacing-none']} !important`,
    },

    '& .MuiAutocomplete-endAdornment': {
      top: selectedItemsCount > 2 ? '16px' : '24px',
    },
  });
